import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Eye, Copy, Check } from 'lucide-react';
import { Badge } from './ui/Badge';
import type { PromptPreviewData } from '@utils/promptAssembler';
import { copyToClipboard } from '@/utils/clipboard';

interface StudioPromptPreviewProps {
  data: PromptPreviewData;
}

export const StudioPromptPreview: React.FC<StudioPromptPreviewProps> = ({ data }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await copyToClipboard(data.userPrompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
    }
  };

  const nonEmptyCount = data.sections.filter(s => s.content).length;

  return (
    <div className="border-l-4 border-l-accent rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        className="flex items-center justify-between w-full text-left py-3 px-4 group hover:bg-slate-900/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Eye size={14} className="text-accent" />
          <span className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em]">Prompt Preview</span>
          <Badge variant="default" size="xs" className="font-mono">{data.charCount} chars</Badge>
          <Badge variant="default-muted" size="xs">{nonEmptyCount} sections</Badge>
        </div>
        {isExpanded ? (
          <ChevronUp size={16} className="text-slate-400 group-hover:text-slate-200 transition-colors" />
        ) : (
          <ChevronDown size={16} className="text-slate-400 group-hover:text-slate-200 transition-colors" />
        )}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-4 animate-in slide-in-from-top-2 duration-300">
          <div className="space-y-3">
            {data.sections.map((section, i) => (
              <div key={i} className="bg-slate-950/60 rounded-xl p-4 border border-slate-800">
                <h4 className="text-[10px] font-black text-accent uppercase tracking-[0.2em] mb-2">{section.label}</h4>
                <pre className="text-[12px] text-slate-300 font-mono whitespace-pre-wrap leading-relaxed max-h-40 overflow-y-auto custom-scrollbar">
                  {section.content || '\u2014'}
                </pre>
              </div>
            ))}
          </div>

          <div className="flex justify-center pt-2">
            <button
              type="button"
              onClick={handleCopy}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl text-[11px] font-black text-slate-300 uppercase tracking-widest transition-colors"
            >
              {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
              {copied ? 'Copied!' : 'Copy Full Prompt'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
